<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12">
        <v-card class="elevation-2 rounded-lg">
          <v-card-title class="headline primary white--text">
            API Keys
          </v-card-title>

          <v-card-text class="pt-6">
            <!-- Introduction -->
            <div class="mb-6">
              <p class="text-body-1 mb-2">
                Use your API key to authenticate requests to the
                {{ $config.productName }} API. This allows you to create and
                manage {{ $config.UNIT_NOUN_PLURAL }} programmatically.
              </p>
              <p class="text-body-2 grey--text">
                Keep your API key secure and never share it publicly. If you
                believe your key has been compromised, contact support to
                regenerate it.
              </p>
            </div>

            <!-- API Key Display -->
            <v-card outlined class="pa-4 api-key-card">
              <div class="d-flex align-center justify-space-between mb-3">
                <div class="text-subtitle-1 font-weight-medium">
                  <v-icon class="mr-2" color="primary">mdi-key</v-icon>
                  Your API Key
                </div>
                <v-chip small :color="apiKey ? 'success' : 'grey'" dark>
                  {{ apiKey ? "Active" : "Loading..." }}
                </v-chip>
              </div>

              <div class="api-key-field">
                <v-text-field
                  :value="showApiKey ? apiKey : maskedApiKey"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="api-key-input"
                >
                  <template v-slot:append>
                    <v-btn
                      icon
                      small
                      @click="showApiKey = !showApiKey"
                      :title="showApiKey ? 'Hide API key' : 'Show API key'"
                    >
                      <v-icon small>{{
                        showApiKey ? "mdi-eye-off" : "mdi-eye"
                      }}</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      @click="copyApiKey"
                      title="Copy to clipboard"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </div>

              <div class="text-caption mt-3 grey--text">
                <v-icon x-small class="mr-1">mdi-information</v-icon>
                Include this key in the <code>Authorization</code> header as
                <code>Bearer YOUR_API_KEY</code>
              </div>
            </v-card>

            <v-divider class="my-6"></v-divider>

            <!-- Quick Start -->
            <div class="mb-4">
              <div class="text-subtitle-1 font-weight-medium mb-3">
                <v-icon class="mr-2" color="primary">mdi-rocket-launch</v-icon>
                Quick Start
              </div>
              <p class="text-body-2 mb-3">
                Test your API key by making a simple request to check your
                credit balance:
              </p>

              <div class="code-block pa-3">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="code-label">cURL</span>
                  <v-btn x-small text @click="copyExample">
                    <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                    Copy
                  </v-btn>
                </div>
                <pre
                  class="code-content mb-0"
                ><code>curl -X GET "{{ apiBaseUrl }}/credits/balance" \
  -H "Authorization: Bearer {{ apiKey || 'YOUR_API_KEY' }}"</code></pre>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Success Snackbar -->
        <v-snackbar v-model="snackbar" :timeout="2000" color="success" top>
          {{ snackbarText }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import config from "@/config";

export default {
  name: "ApiKeysView",
  data() {
    return {
      showApiKey: false,
      snackbar: false,
      snackbarText: ""
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    apiBaseUrl() {
      return config.api.baseURL;
    },
    apiKey() {
      return (this.currentUser && this.currentUser.api_key) || "";
    },
    maskedApiKey() {
      if (!this.apiKey) return "";
      // Show first 8 and last 4 characters
      if (this.apiKey.length > 12) {
        return (
          this.apiKey.substring(0, 8) +
          "..." +
          this.apiKey.substring(this.apiKey.length - 4)
        );
      }
      return "**********************";
    }
  },
  created() {
    // Ensure user data is loaded
    if (!this.currentUser) {
      this.$store.dispatch("fetchCurrentUser");
    }
  },
  methods: {
    copyApiKey() {
      if (!this.apiKey) return;
      navigator.clipboard.writeText(this.apiKey).then(() => {
        this.snackbarText = "API key copied to clipboard";
        this.snackbar = true;
      });
    },
    copyExample() {
      const example = `curl -X GET "${this.apiBaseUrl}/credits/balance" \\
  -H "Authorization: Bearer ${this.apiKey || "YOUR_API_KEY"}"`;
      navigator.clipboard.writeText(example).then(() => {
        this.snackbarText = "Example copied to clipboard";
        this.snackbar = true;
      });
    }
  }
};
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.api-key-card {
  background: linear-gradient(
    135deg,
    rgba(var(--v-primary-base), 0.03),
    rgba(var(--v-primary-base), 0.08)
  );
}

.api-key-input {
  font-family: monospace;
}

.api-key-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
}

.code-block {
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.code-label {
  color: #666;
  font-size: 0.75rem;
}

.code-content {
  color: #333;
  font-family: "Fira Code", "Consolas", monospace;
  font-size: 0.8125rem;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  background: transparent;
  margin: 0;
}

.code-content code {
  color: #333;
  background: transparent;
  font-family: inherit;
}
</style>
